import { useCallback, useEffect, useState } from 'react';
import { MenuItem, Box, styled, Grid } from '@mui/material'
import _, { floor } from 'lodash'
import { Col, Row, Image } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import AppointmentApi, { AppointmentInterface } from 'api/counter/appointment.api'
import ClinicApi, { DentistInterface } from 'api/master/clinic.api'
import InputTextSearch from 'component/Input/InputTextSearch'
import FilterSelect from 'component/Select/FilterSelect'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'
import moment from 'moment'
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker'
import { colors, ThemeIcon } from 'constants/theme'
import DefaultPagination from 'component/Pagination/DefaultPagination'
import UseWindowSize from 'utils/useWindowSize'
import TreatmentApi from 'api/dentists/treatment.api'
import { toBuddhistYear, checkPackage, numberToCurrencyFormat } from 'utils/app.utils'
import { PACKAGE_ADDONS } from 'constants/package'
import { dateTimeToView } from 'utils/date.utils'
import { fileUrl } from 'api/api'
import { imgs, icons } from 'constants/images';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUser } from '@fortawesome/free-solid-svg-icons'
import { BoxStatus, TextStatus } from 'features/home/UseStyled';
import * as TreatmentsUseStyled from 'features/treatments/UseStyle'
import BtnProfile from 'component/Treatments/BtnProfile';
import { showModal } from 'app/slice/modal.slice';
import { useDispatch } from 'react-redux';
import PopupPatientDetail from 'component/Treatments/PopupPatientDetail';

export interface IViewHistoryProps {
  showPopup: boolean
  onClose: () => void
}

type AppointmentHistoryType = {
  type?: 'Treatment' | 'HistoryTreatment'
  patientId?: number
}

/** STYLE CUSTOM */
const TableConfig = styled('div')(({ theme }) => ({
  '> div': {
    '&.custom-scroll::-webkit-scrollbar': {
      height: 8
    }
  },
  '.MuiTable-root': {
    tableLayout: 'fixed',
    [theme.breakpoints.up('lg')]: {
      minWidth: 1770
    }
  },
  th: {
    '&:first-of-type': {
      width: 50
    },
    '&:nth-of-type(2)': {
      width: 150
    },
    '&:nth-of-type(3), &:nth-of-type(4)': {
      width: 130
    },
    '&:nth-of-type(5)': {
      paddingLeft: 4,
      paddingRight: 4,
      width: 155
    },
    '&:nth-of-type(8), &:nth-of-type(9)': {
      width: 120
    },
    '&:nth-of-type(10), &:nth-of-type(11)': {
      width: 170
    },
    '&:last-of-type': {
      padding: '15px',
      width: 100
    }
  },
  td: {
    padding: '15px !important',
    verticalAlign: 'top !important',
    '&.update-by': {
      paddingBottom: '0px !important'
    },
    '&:first-of-type': {
      paddingLeft: '4px !important',
      paddingRight: '4px !important'
    },
  },
  '& .appointmentOperatives': { maxWidth: '200px' },
  '& .practice': { maxWidth: '300px' },
  '& .remark': { maxWidth: '300px' }
}))

const THead = styled('div')(({ theme }) => ({
  display: 'flex',
  backgroundColor: colors.themeMainColor,
  borderRadius: '8px 8px 0 0',
  padding: '0.5rem 1.5rem',
  fontSize: 16,
  color: colors.white
}))

const TBody = styled('div')(({ theme }) => ({
  border: `1px solid ${colors.lightGray}`,
  borderTopWidth: 0,
  color: colors.textPrimary,
  fontSize: 16,
  padding: '0.625rem 1.5rem',
  '.expand-more': {
    backgroundColor: colors.white,
    border: `1px solid ${colors.textLightGray}`,
    color: colors.themeMainColor,
    borderRadius: 100,
    fontSize: 22
  }
}))

const Cell = styled('div')(({ theme }) => ({ '&:first-of-type': { marginRight: '1.25rem', width: '100%' } }))

export default function AppointmentHistory(props: AppointmentHistoryType) {
  const { t } = useTranslation()
  const [width] = UseWindowSize()
  const dispatch = useDispatch()

  const queryParams = new URLSearchParams(window.location.search)
  const patientId = queryParams.get("patientId") || props.patientId

  const [doctors, setDoctors] = useState<DentistInterface[]>([])
  const [appointments, setAppointments] = useState<AppointmentInterface[]>([])
  const [patient, setPatient] = useState<any>();
  const [isShowPopupDetail, setIsShowPopupDetail] = useState<boolean>(false)

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  useEffect(() => { setPageLimit(width >= 1024 ? 10 : 3) }, [width]);
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('date')
  const [sortType, setSortType] = useState('DESC')

  /** FILTER */
  const [search, setSearch] = useState('')
  const [doctor, setDoctor] = useState<number>(0)
  const [rangeDate, setRangeDate] = useState<string[]>(emptyRangePicker)

  const loadData = useCallback(async () => {
    let condition: any = {}
    if (page) condition = { ...condition, page: page }
    if (pageLimit) condition = { ...condition, pageLimit: pageLimit }
    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sortType: sortType }

    if (search) condition = { ...condition, search: search }
    if (doctor) condition = { ...condition, doctorId: doctor }
    if (patientId) condition = { ...condition, patientId: patientId }
    if (rangeDate) {
      if ((rangeDate[0] !== null || rangeDate[0] !== '') && (rangeDate[1] !== null || rangeDate[1] !== ''))
        condition = { ...condition, dateStart: rangeDate[0], dateEnd: rangeDate[1] }
    }
    const patients = await AppointmentApi.findAllHistory(condition)
    if (patients.status === 200) {
      setRowCount(patients.headers['x-total'])
      setAppointments(patients.data)
    }

    ClinicApi.findAllDentists().then(({ data }) => setDoctors(data))
  }, [page, pageLimit, search, sortBy, sortType, doctor, rangeDate, patientId])

  useEffect(() => {
    loadData()
  }, [page, pageLimit, search, sortBy, sortType, doctor, rangeDate, patientId])

  const loadPatient = async () => {
    if (patientId) {
      const patientData = await TreatmentApi.findPatient(Number(patientId))
      if (patientData.status === 200 && !_.isEmpty(patientData.data)) {
        setPatient(patientData.data)
      }
    }
  }

  useEffect(() => {
    if (props.type !== 'HistoryTreatment') loadPatient()
  }, []);

  const statusIcon: any = {
    NORMAL: { icon: <FontAwesomeIcon style={{ color: colors.white }} icon={faUser} />, color: colors.appointmentStatus.statusNormal, label: t('APPOINTMENT.STATUS.NORMAL'), disabled: false },
    CANCEL_APP: { icon: <Image style={{ height: '15px' }} src={icons.appointment.cancel_app} />, color: colors.appointmentStatus.statusCancel, label: t('APPOINTMENT.STATUS.CANCEL'), disabled: false },
    CANCEL: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconCancel} />, color: colors.appointmentStatus.statusCancel, label: t('APPOINTMENT.STATUS.CANCEL'), disabled: false },
    CAN_NOT_CONTACT: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconNotConnect} />, color: colors.appointmentStatus.statusUnConnect, label: t('APPOINTMENT.STATUS.CAN_NOT_CONTACT'), disabled: false },
    PUT_OFF: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconChangeSchedule} />, color: colors.appointmentStatus.statusChange, label: t('APPOINTMENT.STATUS.PUT_OFF'), disabled: false },
    CONFIRM: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconEnter} />, color: colors.appointmentStatus.statusEnter, label: t('APPOINTMENT.STATUS.CONFIRM'), disabled: false },
    ARRIVED: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconUserCheckTrue} />, color: colors.appointmentStatus.statusArrived, label: t('APPOINTMENT.STATUS.ARRIVED'), disabled: false },
    NOT_YET: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconUserCheckFalse} />, color: colors.appointmentStatus.statusUnArrived, label: t('APPOINTMENT.STATUS.NOT_YET'), disabled: false },
    PAYMENT: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconPendingPayment} />, color: colors.appointmentStatus.statusWaitPayment, label: t('APPOINTMENT.STATUS.PENDING_PAYMENT'), disabled: true },
    PENDING_PAYMENT: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconWaitPayment} />, color: colors.appointmentStatus.statusPayment, label: t('APPOINTMENT.STATUS.PAYMENT'), disabled: true },
    PENDING_SERVICE: { icon: <Image style={{ height: '15px' }} src={icons.appointment.iconCounterCheckIn} />, color: colors.appointmentStatus.statusCounterCheckIn, label: t('APPOINTMENT.STATUS.PENDING_SERVICE'), disabled: true },
    SERVICE: { icon: <Image style={{ height: '17px' }} src={icons.appointment.iconService} />, color: colors.appointmentStatus.statusService, label: t('APPOINTMENT.STATUS.SERVICE'), disabled: true },
    DONE: { icon: <Image style={{ height: '15px' }} src={ThemeIcon.DoneTreatment} />, color: colors.appointmentStatus.statusDone, label: t('APPOINTMENT.STATUS.DONE'), disabled: true },
  }

  const renderAppointStatus = (status: string) => {
    return <BoxStatus bg={statusIcon[status]?.color || ''}>
      {statusIcon[status]?.icon || ''}
      <TextStatus  >
        {statusIcon[status]?.label || ''}
      </TextStatus>
    </BoxStatus>
  }

  const renderImage = (img: any) => {
    if (!_.isEmpty(img) && !_.isEmpty(img.filePath) && !_.isEmpty(img.fileName)) return `${fileUrl}/${img.filePath}/${img.fileName}`
    else return imgs.defaultAvatar
  }

  const onViewHistory = () => {
    dispatch(showModal())
    setIsShowPopupDetail(true)
  }

  const headCellsLab = checkPackage(PACKAGE_ADDONS.ADDON_LAB) ? [{ id: 'hasLab', disablePadding: false, label: t('APPOINTMENT.HISTORY.LAB'), width: '85px' }] : []
  const headCellsXray = checkPackage(PACKAGE_ADDONS.ADDON_X_RAY) ? [{ id: 'hasXray', disablePadding: false, label: t('X-Ray'), width: '85px' }] : []
  const headCells = [
    { id: 'no', disablePadding: false, label: t('#') },
    { id: 'branchId', disablePadding: false, label: t('APPOINTMENT.HISTORY.BRANCH') },
    { id: 'date', disablePadding: false, label: t('APPOINTMENT.HISTORY.APPOINTMENT_DATE') },
    { id: 'timeStart', disablePadding: false, label: t('APPOINTMENT.HISTORY.TIME_START') },
    { id: 'durationMinute', disablePadding: false, label: t('APPOINTMENT.HISTORY.APPOINTMENT_DURATION'), align: 'center', class: 'text-wrap' },
    { id: 'doctorFullname', disablePadding: false, label: t('APPOINTMENT.HISTORY.DOCTOR'), width: '180px' },
    { id: 'appointmentOperatives', disablePadding: false, label: t('HISTORY_TREATMENT.TABLE.SERVICE'), width: '250px' },
    { id: 'statusAppointment', disablePadding: false, label: t('APPOINTMENT.HISTORY.STATUS'), align: 'center', width: '150px !important' },
    { id: 'price', disablePadding: false, label: t('APPOINTMENT.HISTORY.PRICE'), width: '130px !important' },
    { id: 'practice', disablePadding: false, label: t('APPOINTMENT.HISTORY.PRACTICE') },
    { id: 'remark', disablePadding: false, label: t('APPOINTMENT.HISTORY.REMARK') },
    ...headCellsLab,
    ...headCellsXray,
    { id: 'updatedBy', disablePadding: false, label: t('APPOINTMENT.HISTORY.CREATED'), width: '150px !important' },
    { id: 'updatedBy', disablePadding: false, label: t('APPOINTMENT.HISTORY.UPDATED'), width: '150px !important' },
  ]

  const getDurationTextByTimeMinute = (minute: number) => {
    if (minute >= 60) return `${floor(minute / 60)} ${t('TIME_DURATION_TYPE.HOURS')} ` + (minute % 60 > 0 ? `${minute % 60} ${t('TIME_DURATION_TYPE.MINUTES')}` : '')
    else return `${minute} ${t('TIME_DURATION_TYPE.MINUTES')}`
  }

  const renderDataSlim = (objData: AppointmentInterface, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { branchName, date, timeStart, durationMinute, doctorFullname, appointmentOperatives, statusAppointment, price, practice, remark, hasLab, hasXray, createdAt, createdBy, updatedAt, updatedBy } = objData
    return (
      <TBody>
        <div className={'d-flex align-items-center position-relative'}>
          <Cell>
            <Row className="pl-4 mx-0">
              <div className="position-absolute w-auto px-0" style={{ marginLeft: '-1.5rem' }}>
                {no}
              </div>
              <Col xs={6} sm={3} className="font-weight-bold">
                {t('APPOINTMENT.HISTORY.BRANCH')}
              </Col>
              <Col xs={6} sm={3}>
                {branchName}
              </Col>
              <Col xs={6} sm={4} className="font-weight-bold">
                {t('APPOINTMENT.HISTORY.APPOINTMENT_DATE')}
              </Col>
              <Col xs={6} sm={2}>
                {date ? toBuddhistYear(moment(date), 'DD/MM/YYYY') : '-'}
              </Col>
              <Col xs={6} sm={3} className="font-weight-bold">
                {t('APPOINTMENT.HISTORY.TIME_START')}
              </Col>
              <Col xs={6} sm={3}>{`${timeStart.replace(':00', '').replace(':', '.')} ${t('TIME_UNIT')}`}</Col>
              <Col xs={6} sm={4} className="font-weight-bold">
                {t('APPOINTMENT.HISTORY.APPOINTMENT_DURATION')}
              </Col>
              <Col xs={6} sm={2}>
                {getDurationTextByTimeMinute(durationMinute)}
              </Col>

              <Col xs={6} sm={3} className="font-weight-bold">
                {t('APPOINTMENT.HISTORY.DOCTOR')}
              </Col>
              <Col xs={6} sm={9}>
                {doctorFullname}
              </Col>

              <Col xs={6} sm={3} className="font-weight-bold">
                {t('HISTORY_TREATMENT.TABLE.SERVICE')}
              </Col>
              <Col xs={6} sm={9}>
                {String(_.map(appointmentOperatives, (op: any) => op.operativeName || op.operativeTypeName).join(', '))}
              </Col>

              <Col xs={6} sm={3} className="font-weight-bold">
                {t('APPOINTMENT.HISTORY.STATUS')}
              </Col>
              <Col xs={6} sm={3}>
                {renderAppointStatus(statusAppointment)}
              </Col>
              <Col xs={6} sm={4} className="font-weight-bold">
                {t('APPOINTMENT.HISTORY.PRICE')}
              </Col>
              <Col xs={6} sm={2}>
                {numberToCurrencyFormat(price)}
              </Col>

              <Col xs={6} sm={3} className="font-weight-bold">
                {t('APPOINTMENT.HISTORY.PRACTICE')}
              </Col>
              <Col xs={6} sm={9} className={'hard-break'}>
                {practice || '-'}
              </Col>

              <Col xs={6} sm={3} className="font-weight-bold">
                {t('APPOINTMENT.HISTORY.REMARK')}
              </Col>
              <Col xs={6} sm={9} className={'hard-break'}>
                {remark || '-'}
              </Col>
              {checkPackage(PACKAGE_ADDONS.ADDON_LAB) && (
                <>
                  <Col xs={6} sm={3} className="font-weight-bold">
                    {t('APPOINTMENT.HISTORY.LAB')}
                  </Col>
                  <Col xs={6} sm={3} className="-font-weight-bold">
                    {t(`${hasLab}`)}
                  </Col>
                </>
              )}
              {checkPackage(PACKAGE_ADDONS.ADDON_X_RAY) && (
                <>
                  <Col xs={6} sm={3} className="font-weight-bold">
                    {t('X-Ray')}
                  </Col>
                  <Col xs={6} sm={3} className="-font-weight-bold">
                    {t(`${hasXray}`)}
                  </Col>
                </>
              )}
              <Col xs={6} sm={3} className="font-weight-bold">
                {t('APPOINTMENT.HISTORY.CREATED')}
              </Col>
              <Col xs={6} sm={3} className="-font-weight-bold">
                {createdBy} {createdAt ? dateTimeToView(createdAt, false) : ''}
              </Col>
              <Col xs={6} sm={3} className="font-weight-bold">
                {t('APPOINTMENT.HISTORY.UPDATED')}
              </Col>
              <Col xs={6} sm={3} className="-font-weight-bold">
                {updatedBy} {updatedAt ? dateTimeToView(updatedAt, false) : ''}
              </Col>
            </Row>
          </Cell>
        </div>
      </TBody>
    )
  }

  const renderData = (objData: AppointmentInterface, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { appointmentId, branchName, date, timeStart, durationMinute, doctorFullname, appointmentOperatives, statusAppointment, price, practice, remark, hasLab, hasXray, createdAt, createdBy, updatedAt, updatedBy } = objData
    const objRenderData = {
      key: String(appointmentId),
      id: String(appointmentId),
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: no },
        { option: 'TEXT', align: 'left', label: branchName || '' },
        { option: 'TEXT', align: 'left', label: date ? toBuddhistYear(moment(date), 'DD/MM/YYYY') : '-' },
        { option: 'TEXT', align: 'left', label: `${timeStart.replace(':00', '').replace(':', '.')} ${t('TIME_UNIT')}` },
        { option: 'TEXT', align: 'center', label: getDurationTextByTimeMinute(durationMinute) },
        { option: 'TEXT', align: 'left', label: doctorFullname },
        { option: 'TEXT', align: 'left', label: String(_.map(appointmentOperatives, (op: any) => op.operativeName || op.operativeTypeName).join(', ')), class: 'appointmentOperatives' },
        { option: 'COMPONENT', align: 'center', component: renderAppointStatus(statusAppointment), class: 'text-nowrap' },
        { option: 'TEXT', align: 'right', label: numberToCurrencyFormat(price) },
        { option: 'TEXT', align: 'left', label: practice || '-', class: 'practice hard-break' },
        { option: 'TEXT', align: 'left', label: remark || '-', class: 'remark hard-break' },
        checkPackage(PACKAGE_ADDONS.ADDON_LAB) && { option: 'TEXT', align: 'left', label: t(`${hasLab}`) },
        checkPackage(PACKAGE_ADDONS.ADDON_X_RAY) && { option: 'TEXT', align: 'left', label: t(`${hasXray}`) },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: createdBy, updatedAt: createdAt } },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },
      ]
    }
    return <TableRowCommon {...objRenderData} />
  }

  return (
    <div>
      {props.type !== 'HistoryTreatment' && (
        <div className="p-3" style={{ boxShadow: `0px 3px 6px ${colors.black08}` }}>
          <h5 className='mb-3' ><strong >{t('HISTORY_APPOINTMENT.APPOINTMENT_LIST')}</strong></h5>
          {/* <p className='mb-1' > <span style={{ fontWeight: 500 }} className='mr-2'>{patient?.branchCnNumber ? `${patient?.branchCnNumber}` : ''} {patient?.firstname} {patient?.lastname}</span>
            <LabelRanking rank={patient?.rank || "BRONZ"} />
          </p> */}
          <Grid item className='w-100'>
            <TreatmentsUseStyled.CardProfile className={`d-flex px-3 ${!patient?.patientId ? 'pe-none' : ''}`} onClick={onViewHistory}>
              <BtnProfile disabled={!patient?.patientId} profile={renderImage(patient?.profile)} branchCnNumber={patient?.branchCnNumber} firstName={patient?.firstname} lastName={patient?.lastname} rankId={patient?.rankId} displayName={patient?.displayName} lastVisit={patient?.updatedAt} />
            </TreatmentsUseStyled.CardProfile>
          </Grid>
        </div>
      )}
      <div className={`${props.type === 'HistoryTreatment' ? 'p-0' : 'p-3'}`}>
        <Row className="mx-0">
          <Col xs={12} sm={6} lg={4} xl={3} className="px-1 pl-sm-2 pr-lg-2 mb-3">
            <InputTextSearch
              keyInput={'search'}
              label={t('HISTORY_APPOINTMENT.SEARCH_OPD')}
              value={search}
              onchange={(event) => {
                setSearch(event.target.value)
                setPage(1)
              }}
              onClear={(event) => {
                setSearch('')
                setPage(1)
              }}
              formStyle={{
                minWidth: '100% !important',
                maxWidth: '100% !important'
              }}
            />
          </Col>
          <Col xs={12} sm={6} lg={4} xl={3} className="px-1 pr-sm-2 pl-lg-2 pr-lg-1 pr-xl-2 mb-3">
            <FilterSelect
              renderValue={() => `${t('HISTORY_APPOINTMENT.DOCTOR')}: ${_.find(doctors, (d: DentistInterface) => d.dentistId === doctor)?.dentistFullname || t('HISTORY_APPOINTMENT.STATUS_ALL')}`}
              label={t('STATUS.LABEL')}
              selectId="select-status"
              value={doctor}
              onchange={(event) => {
                setDoctor(event.target.value)
                setPage(1)
              }}
              labelId="label-status"
              options={[
                <MenuItem key={'all'} value={0}>
                  {t('HISTORY_APPOINTMENT.STATUS_ALL')}
                </MenuItem>,
                _.map(doctors, (doctorData: DentistInterface, i: number) => (
                  <MenuItem key={i} value={doctorData.dentistId}>
                    {doctorData.dentistFullname}
                  </MenuItem>
                ))
              ]}
              formControlStyle={{
                minWidth: '100% !important',
                maxWidth: '100% !important'
              }}
            />
          </Col>
          <Col xs={12} sm={6} lg={4} className="px-1 pl-sm-2 pl-lg-1 pr-lg-2 pl-xl-2 pr-xl-0 mb-3">
            <Box sx={{ maxWidth: { lg: '350px' } }}>
              <InputSecondNewRangePicker
                value={rangeDate || []}
                inputHeight={32}
                onchange={(val: any) => {
                  if (_.isEmpty(val)) setRangeDate(emptyRangePicker)
                  else {
                    setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
                    setPage(1)
                  }
                }}
                allowClear
                onClear={() => setRangeDate(emptyRangePicker)}
              />
            </Box>
          </Col>
        </Row>
        <div className="d-none d-xl-block">
          <TableConfig>
            <TableCustom
              page={page}
              pageLimit={pageLimit}
              sortType={sortType}
              sortBy={sortBy}
              rowCount={rowCount}
              onSort={(sortByVal: string, sortTypeVal: string) => {
                setSortType(sortTypeVal)
                setSortBy(sortByVal)
              }}
              setPageLimit={setPageLimit}
              setPage={setPage}
              headCells={headCells}
              rowsData={_.map(appointments, (val, i) => renderData(val, i))}
              customScroll
              tableFixedWidth
              tableMinWidth={2100}
            />
          </TableConfig>
        </div>
        <div className="d-block d-xl-none">
          <THead>
            <Cell>{t('#')}</Cell>
          </THead>
          <div className={'treatment-body'}>
            {_.map(appointments, renderDataSlim)}
            <div className={'mt-2 '}>
              <DefaultPagination count={rowCount} allRow={rowCount} pageLimit={pageLimit} page={page} setPage={setPage}></DefaultPagination>
            </div>
          </div>
        </div>
      </div>
      <PopupPatientDetail type='historyTreatment' profile={renderImage(patient?.profile)} patient={patient} showPopup={isShowPopupDetail} onClose={() => setIsShowPopupDetail(false)} />
    </div>
  )
}
